/*
  SyncStatusBanner — persistent inline banner listing any integration
  that's disconnected or stale. Replaces the previous transient toast
  approach (users missed them). Polls /api/integrations/status every
  60s. Dismissible per browser session, but re-appears on any newly
  worsening state.
*/
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, XCircle, CheckCircle2, X, ArrowRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const SEV_STYLES = {
  error: "border-red-300 bg-red-50 text-red-900",
  warning: "border-amber-300 bg-amber-50 text-amber-900",
  info: "border-sky-300 bg-sky-50 text-sky-900",
  ok: "border-emerald-300 bg-emerald-50 text-emerald-900",
};
const SEV_ICONS = { error: XCircle, warning: AlertTriangle, info: AlertTriangle, ok: CheckCircle2 };

const DISMISS_KEY = "sync-status-dismissed-v1";

function _loadDismissed() {
  try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "{}"); }
  catch { return {}; }
}
function _saveDismissed(v) {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(v)); } catch {}
}

export default function SyncStatusBanner() {
  const [status, setStatus] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState(_loadDismissed);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const r = await api.get("/integrations/status");
      setStatus(r.data);
    } catch {
      // Silent — a 401 here means user isn't signed in and the whole
      // banner should stay hidden anyway.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  if (!status) return null;
  // Only care about integrations flagged stale AND not currently dismissed.
  const stale = (status.integrations || []).filter((i) => i.stale && i.stale_reason);
  const surfaced = stale.filter((i) => !dismissed[i.key + ":" + (i.stale_reason || "")]);
  if (surfaced.length === 0) return null;

  const worst = status.worst_severity || "warning";
  const Icon = SEV_ICONS[worst] || AlertTriangle;

  function dismissAll() {
    const next = { ...dismissed };
    for (const i of surfaced) {
      next[i.key + ":" + (i.stale_reason || "")] = Date.now();
    }
    setDismissed(next);
    _saveDismissed(next);
  }

  return (
    <div
      className={cn(
        "border rounded-md px-3 py-2 mb-3 shadow-sm",
        SEV_STYLES[worst] || SEV_STYLES.warning,
      )}
      data-testid="sync-status-banner"
    >
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            {surfaced.length === 1
              ? "Integration needs attention"
              : `${surfaced.length} integrations need attention`}
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              className="text-2xs font-normal opacity-70 hover:opacity-100 underline"
              data-testid="sync-status-toggle"
            >
              {collapsed ? "Show details" : "Hide details"}
            </button>
          </div>
          {!collapsed && (
            <ul className="mt-1 space-y-1">
              {surfaced.map((i) => (
                <li key={i.key} className="text-xs flex items-center gap-2"
                    data-testid={`sync-status-${i.key}`}>
                  <span className="font-medium">{i.label}:</span>
                  <span className="truncate">{i.stale_reason}</span>
                  {i.action_href && (
                    <button
                      type="button"
                      onClick={() => navigate(i.action_href)}
                      className="ml-auto inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded border bg-white/60 hover:bg-white"
                      data-testid={`sync-status-fix-${i.key}`}
                    >
                      Fix <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            size="sm" variant="ghost"
            onClick={load}
            className="h-6 w-6 p-0"
            title="Refresh integration status"
            data-testid="sync-status-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={dismissAll}
            className="h-6 w-6 p-0"
            title="Dismiss until the state changes"
            data-testid="sync-status-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
